import { useCallback, useState } from 'react';
import { ActionType, ComponentType, logEvent, markStep } from '@cbhq/client-analytics';

import { rumAddAction } from '../../utils/analytics/datadogRum';
import { logError } from '../../utils/logging';

import type { MemberRole } from './utils/constants';
import type { LegalEntityRole } from './useMultiLEProductSelection';
import { useTeamApi } from './useTeamApi';

type UseEditSubmissionParams = {
  organizationId: string;
  memberUuid: string;
  selectedRoles?: MemberRole[];
  legalEntityIds?: string[];
  legalEntityRoles?: LegalEntityRole[];
  /** Entities the current user may view but not manage; never submitted. */
  readOnlyEntityIds?: string[];
  isFormValid: boolean;
  onSuccess: () => void;
  onFailure: (error: unknown) => void;
};

type UseEditSubmissionReturn = {
  isSaving: boolean;
  saveEdit: (actionProofToken?: string) => Promise<void>;
};

export function useEditSubmission({
  organizationId,
  memberUuid,
  isFormValid,
  onSuccess,
  onFailure,
  selectedRoles,
  legalEntityIds,
  legalEntityRoles,
  readOnlyEntityIds,
}: UseEditSubmissionParams): UseEditSubmissionReturn {
  const { updateMemberRoles, updateMemberRolesPerEntity } = useTeamApi(organizationId);
  const [isSaving, setIsSaving] = useState(false);

  const saveEdit = useCallback(
    async (actionProofToken?: string) => {
      if (isSaving || !isFormValid) {
        return;
      }

      setIsSaving(true);

      const perEntity = legalEntityRoles !== undefined;

      try {
        markStep('submit_workspace_member_update');
        logEvent('member_role_changed', {
          componentType: ComponentType.button,
          action: ActionType.click,
        });

        if (legalEntityRoles !== undefined) {
          await updateMemberRolesPerEntity({
            memberUuid,
            legalEntityRoles,
            readOnlyEntityIds,
            actionProofToken,
          });
        } else {
          await updateMemberRoles({
            memberUuid,
            selectedEntityIds: legalEntityIds ?? [],
            roles: selectedRoles ?? [],
            actionProofToken,
          });
        }

        logEvent('member_role_updated_success', {
          componentType: ComponentType.api,
          action: ActionType.api,
        });
        rumAddAction('member_role_updated_success', { perEntity });
        markStep('workspace_member_update_success');
        setIsSaving(false);
        onSuccess();
      } catch (error) {
        logEvent('member_role_updated_failed', {
          componentType: ComponentType.api,
          action: ActionType.api,
        });
        rumAddAction('member_role_updated_failed', { perEntity });
        logError(error, {
          context: 'useEditSubmission.saveEdit',
          isHandled: true,
          isUserVisible: true,
        });
        setIsSaving(false);
        onFailure(error);
      }
    },
    [
      isFormValid,
      isSaving,
      legalEntityIds,
      legalEntityRoles,
      memberUuid,
      onFailure,
      onSuccess,
      readOnlyEntityIds,
      selectedRoles,
      updateMemberRoles,
      updateMemberRolesPerEntity,
    ],
  );

  return {
    isSaving,
    saveEdit,
  };
}
